if (!customElements.get('user-alliance-payment-methods')) {
  class UserAlliancePaymentMethods extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('[data-ua-payment-form]');
      if (!this.form) return;
      this.fieldset = this.querySelector('[data-ua-payment-fieldset]');
      this.mode = this.querySelector('[data-ua-payment-mode]');
      this.details = this.querySelector('[data-ua-payment-details]');
      this.description = this.querySelector('.userAlliance-payment-methods__description');
      this.status = this.querySelector('[data-ua-payment-status]');
      this.retry = this.querySelector('[data-ua-payment-retry]');
      this.login = this.querySelector('[data-ua-payment-login]');
      this.submitButton = this.querySelector('[data-ua-payment-submit]');
      this.cancelButton = this.querySelector('[data-ua-payment-cancel]');
      this.storeCode = (this.dataset.storeCode || '').trim().toUpperCase();
      if (this.storeCode === 'GB') this.storeCode = 'UK';
      const authSession = window.UserAllianceAuth?.getSession?.() || {};
      this.customerEmail = authSession.accessToken ? String(authSession.email || '').trim() : '';
      this.accessToken = String(authSession.accessToken || '').trim();
      this.authStoreCode = this.accessToken ? String(authSession.storeCode || '').trim().toUpperCase() : '';
      if (this.authStoreCode === 'GB') this.authStoreCode = 'UK';
      this.authenticated = Boolean(this.accessToken && this.customerEmail);
      this.options = [];
      this.saved = { paymentMethod: '', paymentDetails: {} };
      this.drafts = new Map();
      this.activeMethod = '';
      this.listeners = new AbortController();
      const listenerOptions = { signal: this.listeners.signal };
      document.addEventListener('user-alliance:withdraw-state-change', () => this.syncVisibility(), listenerOptions);
      this.mode.addEventListener('change', () => this.changeMode(), listenerOptions);
      this.cancelButton.addEventListener('click', () => this.cancel(), listenerOptions);
      this.retry.addEventListener('click', () => this.load(), listenerOptions);
      this.form.addEventListener('submit', (event) => this.submit(event), listenerOptions);
      document.addEventListener('user-alliance:auth-change', () => {
        const session = window.UserAllianceAuth?.getSession() || {};
        this.customerEmail = session.accessToken ? String(session.email || '').trim() : '';
        this.accessToken = String(session.accessToken || '').trim();
        this.authStoreCode = this.accessToken ? String(session.storeCode || '').trim().toUpperCase() : '';
        if (this.authStoreCode === 'GB') this.authStoreCode = 'UK';
        this.authenticated = Boolean(this.accessToken && this.customerEmail);
        this.syncVisibility();
        this.requestController?.abort();
        if (!this.authenticated) {
          this.ready = false;
          this.options = [];
          this.saved = { paymentMethod: '', paymentDetails: {} };
          this.setBusy(false);
          this.setStatus('');
          return;
        }
        this.saved = { paymentMethod: '', paymentDetails: {} };
        this.restoreSaved();
        this.load();
      }, listenerOptions);
      this.syncVisibility();
      if (this.authenticated) this.load();
    }

    syncVisibility() {
      const withdrawButton = document.querySelector('[data-ua-withdraw-button]');
      this.hidden = !this.authenticated || !withdrawButton || withdrawButton.disabled;
    }

    disconnectedCallback() {
      this.listeners?.abort();
      this.requestController?.abort();
    }

    async request(endpoint, body, signal) {
      const response = await window.UserAllianceAuth.request(endpoint, {
        method: 'POST',
        credentials: 'omit',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        signal
      });
      if (!response.ok) throw new Error('Payment request failed');
      const payload = await response.json();
      if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
      if (!payload || (payload.code !== undefined && Number(payload.code) !== 0)) {
        throw new Error('Payment request failed');
      }
      return payload.data ?? payload;
    }

    async load() {
      this.requestController?.abort();
      const controller = new AbortController();
      this.requestController = controller;
      this.ready = false;
      this.setBusy(true);
      this.retry.hidden = true;
      this.login.hidden = true;
      this.setStatus('Loading payment methods...');
      try {
        if (!this.authStoreCode) throw new Error('Missing store code');
        const data = await this.request(this.dataset.optionsEndpoint, { storeCode: this.authStoreCode }, controller.signal);
        const options = data?.options;
        this.options = Array.isArray(options)
          ? options.map((option) => this.normaliseOption(option)).filter(Boolean)
          : [];
        this.mode.replaceChildren(new Option(this.dataset.modePlaceholder || 'No payment mode set', ''));
        this.renderFields({});
        this.options.forEach((option) => this.mode.add(new Option(option.label || option.code, option.code)));
        if (!this.options.length) {
          this.setStatus('No payment methods are currently available.', 'error');
          this.retry.hidden = false;
          return;
        }

        if (this.customerEmail) {
          try {
            const saved = await this.request(this.dataset.queryEndpoint, {
              storeCode: this.authStoreCode,
              email: this.customerEmail,
              accessToken: this.accessToken
            }, controller.signal);
            this.saved = {
              paymentMethod: typeof saved.paymentMethod === 'string' ? saved.paymentMethod : '',
              paymentDetails: saved.paymentDetails && typeof saved.paymentDetails === 'object' ? saved.paymentDetails : {}
            };
          } catch (error) {
            if (controller.signal.aborted) throw error;
            this.saved = { paymentMethod: '', paymentDetails: {} };
          }
        }
        this.restoreSaved();
        this.ready = true;
        this.setStatus('');
      } catch (error) {
        if (controller.signal.aborted) return;
        this.setStatus('Unable to load payment settings. Please try again.', 'error');
        this.retry.hidden = false;
      } finally {
        if (this.requestController === controller && !controller.signal.aborted) this.setBusy(false);
      }
    }

    changeMode() {
      this.drafts.set(this.activeMethod, this.readDetails());
      this.activeMethod = this.mode.value;
      this.renderFields(this.drafts.get(this.activeMethod) || {});
      this.submitButton.disabled = !this.mode.value;
      this.setStatus('');
    }

    normaliseOption(option) {
      if (typeof option === 'string') {
        const code = option.trim();
        return code ? { code, label: code, detailFields: [] } : null;
      }
      if (!option || typeof option !== 'object') return null;
      const code = String(option.code ?? option.value ?? option.paymentMethod ?? '').trim();
      if (!code) return null;
      const detailFields = Array.isArray(option.detailFields)
        ? option.detailFields.map((field) => this.normaliseDetailField(field)).filter(Boolean)
        : [];
      return {
        ...option,
        code,
        label: String(option.label ?? option.name ?? option.displayName ?? code),
        detailFields
      };
    }

    normaliseDetailField(field) {
      if (!field || typeof field !== 'object') return null;
      const key = String(field.key ?? field.name ?? field.fieldName ?? '').trim();
      if (!key) return null;
      return {
        ...field,
        key,
        label: String(field.label ?? field.fieldLabel ?? field.displayName ?? key),
        type: String(field.type ?? field.fieldType ?? 'text').toLowerCase(),
        required: field.required === true || field.required === 1 || field.required === 'true'
      };
    }

    getFields() {
      const option = this.options.find((item) => item.code === this.mode.value);
      const emailField = { key: 'email', label: this.dataset.emailLabel, type: 'email', required: true };
      if (!option) return [];
      const detailFields = Array.isArray(option.detailFields) ? option.detailFields : [];
      if (option.code.toLowerCase() === 'bank') {
        return [emailField, ...detailFields.filter((field) => field.key.toLowerCase() !== 'email')];
      }
      if (detailFields.length) return detailFields;
      return option.code.toLowerCase() === 'paypal' ? [emailField] : [];
    }

    renderFields(values) {
      this.details.replaceChildren();
      this.details.hidden = !this.mode.value;
      if (this.description) this.description.hidden = !this.mode.value;
      const fragment = document.createDocumentFragment();
      this.getFields().forEach((field, index) => {
        if (!field || typeof field.key !== 'string' || !field.key) return;
        const wrapper = document.createElement('div');
        wrapper.className = 'userAlliance-payment-methods__field';
        const label = document.createElement('label');
        const id = `${this.id}-detail-${index}`;
        label.htmlFor = id;
        label.textContent = field.label || field.key;
        const choices = Array.isArray(field.options) ? field.options : [];
        const control = document.createElement(field.type === 'select' || choices.length ? 'select' : 'input');
        if (control.tagName === 'SELECT') {
          control.add(new Option('Select an option', ''));
          choices.forEach((choice) => {
            if (choice?.value !== undefined) control.add(new Option(choice.label || String(choice.value), String(choice.value)));
          });
        } else {
          // Account numbers stay text to preserve leading zeros.
          control.type = ['email', 'tel'].includes(field.type) ? field.type : 'text';
          control.autocomplete = control.type === 'email' ? 'email' : 'off';
          if (control.type === 'email') {
            control.placeholder = this.dataset.emailPlaceholder || '';
            control.setAttribute('aria-describedby', `${this.id}-description`);
          }
          if (field.type === 'number') control.inputMode = 'numeric';
        }
        control.id = id;
        control.name = field.key;
        control.dataset.paymentDetail = field.key;
        control.required = true;
        control.value = String(values[field.key] ?? (control.type === 'email' ? this.customerEmail : ''));
        control.addEventListener('input', () => {
          if (control.value.trim()) control.setCustomValidity('');
        });
        wrapper.append(label, control);
        fragment.append(wrapper);
      });
      this.details.append(fragment);
    }

    readDetails() {
      return Object.fromEntries(Array.from(this.details.querySelectorAll('[data-payment-detail]'), (input) => [
        input.dataset.paymentDetail, input.value.trim()
      ]));
    }

    validateDetails() {
      let valid = true;
      this.details.querySelectorAll('[data-payment-detail]').forEach((control) => {
        const empty = !control.value.trim();
        control.setCustomValidity(empty ? 'This field is required.' : '');
        if (empty) valid = false;
      });
      return valid;
    }

    restoreSaved() {
      this.drafts.clear();
      this.mode.value = this.saved.paymentMethod;
      if (!this.mode.value) this.mode.value = '';
      this.activeMethod = this.mode.value;
      this.renderFields(this.saved.paymentDetails);
      this.submitButton.disabled = !this.mode.value;
    }

    cancel() {
      this.restoreSaved();
      this.setStatus('');
      this.login.hidden = true;
      this.mode.focus();
    }

    async submit(event) {
      event.preventDefault();
      if (!this.ready || this.busy) return;
      this.validateDetails();
      if (!this.form.reportValidity()) return;
      if (!this.customerEmail) {
        this.setStatus('Please sign in to save your payment details.', 'error');
        this.login.hidden = false;
        return;
      }

      const paymentMethod = this.mode.value;
      const paymentDetails = this.readDetails();
      const controller = new AbortController();
      this.requestController = controller;
      this.setBusy(true);
      this.setStatus('Saving payment details...');
      try {
        const accessToken = this.accessToken || window.UserAllianceAuth?.getSession?.().accessToken;
        if (controller.signal.aborted) return;
        if (typeof accessToken !== 'string' || !accessToken.trim()) {
          this.setStatus('Please sign in to your affiliate account before saving.', 'error');
          this.login.hidden = false;
          return;
        }
        const data = await this.request(this.dataset.saveEndpoint, {
          storeCode: this.authStoreCode,
          accessToken,
          email: this.customerEmail,
          paymentMethod,
          paymentDetails
        }, controller.signal);
        if (data.configured === false) throw new Error('Payment details were not saved');
        this.saved = { paymentMethod, paymentDetails };
        this.drafts.clear();
        this.setStatus('Your payment details have been saved.', 'success');
        this.dispatchEvent(new CustomEvent('user-alliance:payment-saved', { bubbles: true }));
      } catch (error) {
        if (!controller.signal.aborted) this.setStatus('Unable to save payment details. Please try again.', 'error');
      } finally {
        if (this.requestController === controller && !controller.signal.aborted) this.setBusy(false);
      }
    }

    setBusy(busy) {
      this.busy = busy;
      this.form.setAttribute('aria-busy', String(busy));
      this.fieldset.disabled = busy || !this.ready;
      this.submitButton.disabled = busy || !this.ready || !this.mode.value;
    }

    setStatus(message, state = '') {
      this.status.textContent = message;
      this.status.hidden = !message;
      this.status.classList.toggle('is-error', state === 'error');
      this.status.classList.toggle('is-success', state === 'success');
    }
  }

  customElements.define('user-alliance-payment-methods', UserAlliancePaymentMethods);
}
